import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../theme';

export default function PodcastArchive({ pb, currentTheme = theme.dark }) {
    const navigate = useNavigate();
    const t = currentTheme || theme.dark;

    const [availableRooms, setAvailableRooms] = useState([]);
    const [selectedRoomId, setSelectedRoomId] = useState('');
    const [roomMeta, setRoomMeta] = useState({ title: '', description: '' });
    const [history, setHistory] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const transcriptEndRef = useRef(null);

    // 1. Authentication & Room List Fetch
    useEffect(() => {
        if (!pb || !pb.authStore.isValid) {
            navigate('/');
            return;
        }

        const fetchRooms = async () => {
            try {
                // Fetch unique roomIds from transcripts or rooms collection
                const records = await pb.collection('transcripts').getFullList({
                    sort: '-created',
                    fields: 'roomId',
                });
                const uniqueRooms = [...new Set(records.map(r => r.roomId))];
                setAvailableRooms(uniqueRooms);

                if (uniqueRooms.length > 0) {
                    setSelectedRoomId(uniqueRooms[0]);
                }
            } catch (error) {
                console.error("Failed to load archive rooms:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchRooms();
    }, [navigate, pb]);

    // 2. Fetch Room Meta and Transcript when Selected Room Changes
    useEffect(() => {
        if (!selectedRoomId || !pb) return;

        const fetchRoomData = async () => {
            setIsLoading(true);
            try {
                // Fetch room metadata (title/description)
                const roomRecords = await pb.collection('rooms').getFullList({
                    filter: `roomId = "${selectedRoomId}"`
                });
                if (roomRecords.length > 0) {
                    setRoomMeta({ 
                        title: roomRecords[0].title, 
                        description: roomRecords[0].description 
                    });
                } else {
                    setRoomMeta({ title: `Session ${selectedRoomId}`, description: '' });
                }

                // Fetch transcript history
                const transcripts = await pb.collection('transcripts').getFullList({
                    filter: `roomId = "${selectedRoomId}"`,
                    sort: 'created',
                });
                setHistory(transcripts);
            } catch (error) {
                console.error(`Failed to load archive for ${selectedRoomId}:`, error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchRoomData();
    }, [selectedRoomId, pb]);

    // Auto-scroll transcript container on update
    useEffect(() => {
        transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [history]);

    if (!pb || !pb.authStore.isValid) return null;

    return (
        <div style={{ minHeight: '100vh', background: t.background, color: t.textPrimary, padding: '40px 20px', fontFamily: 'system-ui, -apple-system, sans-serif', transition: 'background 0.3s, color 0.3s' }}>
            <div style={{ maxWidth: '850px', margin: '0 auto' }}>
                
                {/* Header with Navigation and Selector */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', flexWrap: 'wrap', gap: '15px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <h2 style={{ margin: 0, fontSize: '1.8rem', fontWeight: '600' }}>Podcast Archive</h2>
                        <button
                            onClick={() => navigate(selectedRoomId ? `/room/${selectedRoomId}` : '/')}
                            style={{ padding: '8px 14px', background: t.border, color: t.textPrimary, border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}
                        >
                            ← Back to Room
                        </button>
                    </div>

                    {/* Room Selector Dropdown */}
                    <select 
                        value={selectedRoomId} 
                        onChange={(e) => setSelectedRoomId(e.target.value)} 
                        style={{ padding: '10px 14px', borderRadius: '6px', border: `1px solid ${t.border}`, background: t.inputBg, color: t.textPrimary, fontSize: '14px', minWidth: '220px', outline: 'none' }}
                    >
                        <option value="" disabled>Select a session...</option>
                        {availableRooms.map(room => (
                            <option key={room} value={room}>Session: {room}</option>
                        ))}
                    </select>
                </div>

                {/* Session Meta Card */}
                {selectedRoomId && (
                    <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: '10px', padding: '16px 20px', marginBottom: '20px' }}>
                        <h3 style={{ margin: '0 0 5px 0', fontSize: '1.2rem' }}>{roomMeta.title || `Session ${selectedRoomId}`}</h3>
                        <p style={{ margin: 0, color: t.textSecondary, fontSize: '0.95rem' }}>{roomMeta.description || 'Archived live conversation session.'}</p>
                    </div>
                )}

                {/* Scrollable Transcript Feed */}
                {isLoading ? (
                    <p style={{ textAlign: 'center', color: t.textSecondary, padding: '40px' }}>Loading session records...</p>
                ) : (
                    <div style={{ 
                        border: `1px solid ${t.border}`, 
                        height: '450px', 
                        overflowY: 'auto', 
                        padding: '20px', 
                        background: t.cardBg, 
                        borderRadius: '10px', 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: '12px' 
                    }}>
                        {history.length === 0 ? (
                            <p style={{ color: t.textSecondary, textAlign: 'center', marginTop: '150px' }}>No transcript data found for this session.</p>
                        ) : (
                            history.map((item) => {
                                const isAi = item.speaker.includes('AI') || item.speaker === 'Atlas';
                                return (
                                    <div key={item.id} style={{ padding: '12px 16px', background: isAi ? t.aiBubble : t.userBubble, border: `1px solid ${t.border}`, borderRadius: '8px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                            <span style={{ fontWeight: '600', color: isAi ? '#c084fc' : t.textPrimary, fontSize: '0.9rem' }}>
                                                {item.speaker}
                                            </span>
                                            <span style={{ fontSize: '0.75rem', color: t.textSecondary }}>
                                                {new Date(item.created).toLocaleTimeString()}
                                            </span>
                                        </div>
                                        <div style={{ color: t.textPrimary, fontSize: '0.95rem', lineHeight: '1.4' }}>
                                            {item.message}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                        <div ref={transcriptEndRef} />
                    </div>
                )}
            </div>
        </div>
    );
}